import { LanguageSwitcher, PortfolioNav, ThemeSwitcher, UtilityRoutePageShell, buildPortfolioProducts, type PortfolioNavLink } from '@saas-pages/shared/public/app-shell'
import { buildUtilityNavItems, resolveUtilitySourceToolKey } from '@saas-pages/shared/public/registry'
import { Seo } from '@saas-pages/shared/public/seo'
import branding from '../../branding.config'
import { AudioTrimmer } from '@/features/audio-trimmer'
import { CircleVideoRecorder, VoiceRecorder } from '@/features/recorder'
import { BlogIndexPage } from '@/pages/blog/BlogIndexPage'
import { BlogPostPage } from '@/pages/blog/BlogPostPage'
import { IndexPage } from '@/pages/index/IndexPage'
import { seoFor } from '@/seo/routes'
import { locales, useLocale, useT } from '@/shared/i18n'
import { audioTrimmerRoute, circleVideoRoute, mediatikUtilityRegistry, type RouteKey } from '@/shared/utility-registry'

interface AppProps { pathname?: string }

export function App({ pathname }: AppProps = {}) {
  const requestedPath = currentPath(pathname)
  const current = currentRoute(requestedPath)
  const route = current.kind === 'tool' ? current.route : undefined
  const isIndex = current.kind === 'index'
  const isBlogIndex = current.kind === 'blog'
  const isBlogPost = current.kind === 'blogPost'
  const routeKey: RouteKey = route?.routeKey ?? 'voice-recorder'
  const seo = seoFor(isIndex ? 'index' : isBlogIndex || isBlogPost ? 'blog' : routeKey)
  const t = useT()
  const { locale, setLocale } = useLocale()
  const displayRegistry = locale === 'ru' ? mediatikUtilityRegistry : mediatikUtilityRegistry.map((item) => {
    if (item.routeKey === 'audio-trimmer') return {
      ...item,
      title: t.audioTrim.title,
      description: t.audioTrim.description,
      processing: { ...item.processing, label: 'Local in your browser', title: 'The file stays in your browser and is never sent to a server' },
      schema: {
        ...item.schema,
        howTo: ['Choose one audio file up to 25 MB.', 'Set the fragment start and end, then run the trim.', 'Preview and download the result in the original codec and container.'],
        faq: [
          { question: 'Is the audio file sent to a server?', answer: 'No. Inspection, trimming, preview, and export happen locally in your browser.' },
          { question: 'Will the format or quality change?', answer: 'No. The tool copies encoded packets and preserves the original codec and container without transcoding.' },
          { question: 'Why can the boundary move?', answer: 'WAV snaps to PCM blocks. MP3 may move the start back by up to one second to include an independently decodable frame; the actual range is shown.' },
        ],
      },
      relatedTools: item.relatedTools.map((related) => ({ ...related, label: related.routeKey === 'voice-recorder' ? 'Voice recorder' : 'Circular video' })),
    }
    const copy = item.routeKey === 'voice-recorder' ? t.voice : t.circle
    return {
      ...item,
      title: copy.title,
      description: copy.description,
      processing: { ...item.processing, ...copy.processing },
      schema: { ...item.schema, howTo: copy.howTo, faq: copy.faq },
      relatedTools: item.relatedTools.map((related) => ({ ...related, ...copy.related })),
    }
  }) as unknown as typeof mediatikUtilityRegistry
  const tools = buildUtilityNavItems(mediatikUtilityRegistry, routeKey)
  const navItems: PortfolioNavLink[] = [
    { key: 'index', label: t.nav.all, href: '/', current: isIndex },
    ...tools.map((item) => ({ ...item, label: item.key === 'voice-recorder' ? t.nav.voice : item.key === 'audio-trimmer' ? t.audioTrim.title : t.nav.circle, current: !isIndex && !isBlogIndex && !isBlogPost && item.current })),
    { key: 'blog', label: t.nav.blog, href: '/blog', current: isBlogIndex || isBlogPost },
  ]
  const page = isBlogIndex
    ? <BlogIndexPage />
    : isBlogPost
      ? <BlogPostPage slug={current.slug} />
      : isIndex
        ? <IndexPage />
        : null

  return (
    <div className="min-h-screen bg-background text-foreground">
      {isIndex && <Seo metadata={{ ...seo.metadata, openGraph: { ...seo.metadata.openGraph, image: branding.ogImage }, twitter: { ...seo.metadata.twitter, image: branding.ogImage, card: 'summary_large_image' } }} />}
      <PortfolioNav
        brandLabel="mediatik.ru" brandHref="/" products={buildPortfolioProducts('mediatik')} items={navItems}
        sourceToolKey={resolveUtilitySourceToolKey(mediatikUtilityRegistry, requestedPath)}
        actions={<><ThemeSwitcher storageKey="mediatik.theme" /><LanguageSwitcher locales={locales} locale={locale} onLocaleChange={setLocale} labels={{ ru: 'RU', en: 'EN' }} ariaLabel="Language / Язык" /></>}
      />
      <main className="px-4 py-4 sm:py-8">
        {isIndex || isBlogIndex || isBlogPost ? page : (
          <UtilityRoutePageShell
            registry={displayRegistry} routeKey={routeKey} seo={seo} domain={branding.domain} siteName={branding.domain} ogImage={branding.ogImage}
            homeLabel={locale === 'ru' ? 'Медиаинструменты' : 'Media tools'} scenariosLabel={locale === 'ru' ? 'Сценарии' : 'Use cases'}
            howToLabel={locale === 'ru' ? 'Как пользоваться' : 'How to use'} faqLabel={locale === 'ru' ? 'Вопросы' : 'FAQ'}
            otherToolsLabel={locale === 'ru' ? 'Другие инструменты' : 'Other tools'} allToolsLabel={locale === 'ru' ? 'Все инструменты' : 'All tools'}
            categoryLabel={routeKey === circleVideoRoute.routeKey ? (locale === 'ru' ? 'Видео' : 'Video') : (locale === 'ru' ? 'Аудио' : 'Audio')}
            categoryClassName="border-rose-300 bg-rose-50 text-rose-950 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100"
            useCases={routeKey === circleVideoRoute.routeKey ? t.circle.useCases : routeKey === audioTrimmerRoute.routeKey ? (locale === 'ru' ? ['Файл до 25 МБ', 'Без перекодирования', 'Скачивание'] : ['File up to 25 MB', 'No transcoding', 'Download']) : t.voice.useCases}
            icon={routeKey === circleVideoRoute.routeKey ? '◉' : routeKey === audioTrimmerRoute.routeKey ? '✂' : '●'}
          >
            {routeKey === circleVideoRoute.routeKey ? <CircleVideoRecorder /> : routeKey === audioTrimmerRoute.routeKey ? <AudioTrimmer locale={locale} /> : <VoiceRecorder />}
          </UtilityRoutePageShell>
        )}
      </main>
    </div>
  )
}

function currentPath(staticPathname?: string): string {
  if (!staticPathname && typeof window === 'undefined') return '/'
  return (staticPathname ?? window.location.pathname).replace(/\/+$/, '') || '/'
}

type MediatikRoute = (typeof mediatikUtilityRegistry)[number]
type CurrentRoute =
  | { kind: 'index' }
  | { kind: 'blog' }
  | { kind: 'blogPost'; slug: string }
  | { kind: 'tool'; route: MediatikRoute }

function currentRoute(pathname: string): CurrentRoute {
  if (pathname === '/') return { kind: 'index' }
  if (pathname === '/blog') return { kind: 'blog' }
  if (pathname.startsWith('/blog/')) {
    return { kind: 'blogPost', slug: safeDecodeSlug(pathname.slice('/blog/'.length)) }
  }

  const route = mediatikUtilityRegistry.find((item) => item.path === pathname)
  return route ? { kind: 'tool', route } : { kind: 'index' }
}

function safeDecodeSlug(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
